import React from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";
import {
  modifyBotExitConditions,
  resetBotExitConditions,
} from "../../../actions/bot-action";
import { updateRentBot } from "../../../actions/create-bot-action";
import {
  CHANGE_RENT_STOP_LOSS,
  CHANGE_RENT_TAKE_PROFIT,
  UPDATE_RENT_EXIT_CONDITIONS,
} from "../../../actions/create-bot-type";
import { isProMasterYearly } from "../../../utils/SubUtils";
import Loading from "../../common/Loading";
import useLoading from "../../hooks/useLoading";
import LeftRightForm from "../../bots/new-bot/modules/LeftRightForm";
import SliderView from "../../bots/new-bot/SliderView";

const maxPercent = 100;

const formatDecimal = (value, decimals = 2) => {
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return 0;
  }
  return Number(numberValue.toFixed(decimals));
};

const formatPercent = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) {
    return "-";
  }

  return `${numberValue}%`;
};

const getExitConditionState = (result, rentBot) => {
  const bot = result?.bot || {};
  const effective = result?.effective_exit_conditions || {};
  const template = result?.template_exit_conditions || {};

  return {
    takeProfit: effective.profit ?? bot.profit ?? rentBot.takeProfit,
    stoploss: effective.stop_loss ?? bot.stop_loss ?? rentBot.stoploss,
    minTp: effective.min_tp ?? bot.min_tp ?? rentBot.minTp,
    extraPriceGapMin:
      effective.extra_price_gap_min ??
      bot.extra_price_gap_min ??
      rentBot.extraPriceGapMin,
    templateExitConditions: template,
    effectiveExitConditions: effective,
    marketplaceExitConditionsModified: Boolean(
      bot.marketplace_exit_conditions_modified
    ),
  };
};

const RentExitConditionsView = (props) => {
  const {
    rentBot,
    t,
    step,
    modifyBotExitConditions,
    resetBotExitConditions,
    updateRentBot,
  } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const [setLoading, bindLoading] = useLoading(false);
  const subscription = useSelector(
    (state) => state.auth.user && state.auth.user.user_subscription
  );
  const itemsPerPage = useSelector((state) => state.pagination.botItemsPerPage) || 12;
  const selectedExchangeId =
    useSelector((state) => state.settings.selectedExchangeId) || 0;
  const professional = isProMasterYearly(subscription);
  const selectedTab = "active";
  const effective = rentBot.effectiveExitConditions || {};
  const template = rentBot.templateExitConditions || {};

  const changeTakeProfit = (value) => {
    if (Number(value) > maxPercent) {
      return;
    }

    dispatch({
      type: CHANGE_RENT_TAKE_PROFIT,
      payload: value,
    });
  };

  const changeStopLoss = (value) => {
    if (Number(value) > maxPercent) {
      return;
    }

    dispatch({
      type: CHANGE_RENT_STOP_LOSS,
      payload: value,
    });
  };

  const updateExitConditionState = (result) => {
    dispatch({
      type: UPDATE_RENT_EXIT_CONDITIONS,
      payload: getExitConditionState(result, rentBot),
    });
  };

  const resetToTemplate = () => {
    setLoading(true);
    resetBotExitConditions(rentBot.id)
      .then((res) => {
        updateExitConditionState(res);
        toast.success("Exit conditions reset.");
      })
      .catch((err) => toast.error(err))
      .finally(() => setLoading(false));
  };

  const updateBotSettings = () => {
    if (professional && (rentBot.takeProfit === "" || rentBot.stoploss === "")) {
      toast.error("Please enter values for Take Profit and Stop Loss");
      return;
    }

    setLoading(true);
    updateRentBot(itemsPerPage, 1, selectedTab, selectedExchangeId)
      .then(() => {
        if (!professional) {
          return null;
        }

        return modifyBotExitConditions(
          rentBot.id,
          rentBot.takeProfit,
          rentBot.stoploss
        );
      })
      .then((res) => {
        if (res) {
          updateExitConditionState(res);
        }
        toast.success(t("bot-updated-msg"));
        history.push("/bots");
      })
      .catch((err) => toast.error(err))
      .finally(() => setLoading(false));
  };

  return (
    <Loading {...bindLoading}>
      <div className="ba-asset-form-content">
        <div className="row gx-0 gy-3">
          {!professional ? (
            <LeftRightForm
              left={<h6>Exit Conditions</h6>}
              right={
                <p className="rent-exit-condition-text mb-0">
                  This feature is only available to Professional Annual subscribers
                </p>
              }
            />
          ) : (
            <>
              <div className="col-lg-12">
                <div className="rent-exit-warning-bubble">
                  Changing the exit conditions may affect the performance of this strategy
                </div>
              </div>
              <SliderView
                title={t("tp")}
                step={0.5}
                min={0}
                max={maxPercent}
                value={formatDecimal(rentBot.takeProfit)}
                onChange={changeTakeProfit}
                hideLabel={true}
                withInput={true}
              />
              <SliderView
                title={t("Stop Loss")}
                step={0.5}
                min={0}
                max={maxPercent}
                value={formatDecimal(rentBot.stoploss)}
                onChange={changeStopLoss}
                hideLabel={true}
                withInput={true}
              />
           
              {(template.profit !== undefined || template.stop_loss !== undefined) && (
                <LeftRightForm
                  left={<h6>Default Strategy Settings</h6>}
                  right={
                    <p className="rent-exit-condition-text mb-0">
                      TP: {formatPercent(template.profit)} | SL:{" "}
                      {formatPercent(template.stop_loss)}
                    </p>
                  }
                />
              )}
              {rentBot.marketplaceExitConditionsModified && (
                <LeftRightForm
                  left={<h6>Status</h6>}
                  right={
                    <p className="rent-exit-condition-text modified mb-0">
                      Modified from template
                    </p>
                  }
                />
              )}
            </>
          )}
          <div className="col-lg-12">
            <div className="bas-asset-submit bas-asset-btn2 bas-asset-btn3">
              <button type="button" onClick={() => step("prev")}>
                Previous
              </button>
              {professional && (
                <button type="button" onClick={resetToTemplate}>
                  Reset to Default
                </button>
              )}
              <button type="button" onClick={updateBotSettings}>
                {t("update-bot")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Loading>
  );
};

export default connect(null, {
  modifyBotExitConditions,
  resetBotExitConditions,
  updateRentBot,
})(RentExitConditionsView);
